import { makeAutoObservable, ObservableMap } from "mobx";

export interface DownloadProgress {
    attachment_id: string;
    loaded: number;
    total: number | undefined;
}

export interface DownloadState {
    status: AttachmentStatus;
    progress: number | undefined;
}

class DownloadStore {
    private downloads: ObservableMap<string, DownloadState>;
    private progressListeners: Set<(data: DownloadProgress) => void>;
    private downloadedListeners: Set<(data: { attachment_id: string }) => void>;
    private errorListeners: Set<
        (data: { attachment_id: string; error: string }) => void
    >;
    private pendingOpen: string | undefined;
    private pendingShowInFolder: string | undefined;

    constructor() {
        this.downloads = new ObservableMap();
        this.progressListeners = new Set();
        this.downloadedListeners = new Set();
        this.errorListeners = new Set();
        makeAutoObservable(this);
    }

    get(id: string): DownloadState | undefined {
        return this.downloads.get(id);
    }

    getAll(): Map<string, DownloadState> {
        return this.downloads;
    }

    async checkStatus(id: string): Promise<AttachmentStatus | undefined> {
        if (!window.isNative || !window.native?.getAttachmentStatus) {
            return undefined;
        }

        try {
            const status = await window.native.getAttachmentStatus(id);
            this.updateStatus(id, status);
            return status;
        } catch {
            return undefined;
        }
    }

    setDownloading(id: string, path: string) {
        this.downloads.set(id, {
            status: { status: "downloading", path },
            progress: 0,
        });
    }

    updateProgress(data: DownloadProgress) {
        const current = this.downloads.get(data.attachment_id);
        if (!current) {
            this.downloads.set(data.attachment_id, {
                status: { status: "downloading", path: "" },
                progress: 0,
            });
        }

        const updated = this.downloads.get(data.attachment_id)!;
        const currentStatus = updated.status;
        const currentPath =
            currentStatus.status === "downloading" ||
            currentStatus.status === "downloaded"
                ? (
                      currentStatus as {
                          status: "downloading" | "downloaded";
                          path: string;
                      }
                  ).path
                : "";
        updated.status = { status: "downloading", path: currentPath };

        if (data.total) {
            updated.progress = data.loaded / data.total;
        } else {
            updated.progress = undefined;
        }
    }

    setDownloaded(id: string, path: string) {
        this.downloads.set(id, {
            status: { status: "downloaded", path },
            progress: 1,
        });
    }

    updateStatus(id: string, status: AttachmentStatus) {
        const current = this.downloads.get(id);
        const hasCurrent = !!current;

        if (status.status === "not_downloaded") {
            if (hasCurrent) {
                current.status = { status: "not_downloaded" };
                current.progress = undefined;
            } else {
                this.downloads.set(id, {
                    status: { status: "not_downloaded" },
                    progress: undefined,
                });
            }
        } else if (status.status === "downloading") {
            if (hasCurrent) {
                current.status = { status: "downloading", path: status.path };
            } else {
                this.downloads.set(id, {
                    status: { status: "downloading", path: status.path },
                    progress: 0,
                });
            }
        } else if (status.status === "downloaded") {
            if (hasCurrent) {
                current.status = { status: "downloaded", path: status.path };
                current.progress = 1;
            } else {
                this.downloads.set(id, {
                    status: { status: "downloaded", path: status.path },
                    progress: 1,
                });
            }
        }
    }

    setError(id: string) {
        const current = this.downloads.get(id);
        if (!current) return;

        current.status = { status: "not_downloaded" };
        current.progress = undefined;
    }

    clear(id: string) {
        this.downloads.delete(id);
    }

    requestOpen(id: string) {
        this.pendingOpen = id;
    }

    requestShowInFolder(id: string) {
        this.pendingShowInFolder = id;
    }

    onProgress(cb: (data: DownloadProgress) => void) {
        this.progressListeners.add(cb);
        return () => this.progressListeners.delete(cb);
    }

    onDownloaded(cb: (data: { attachment_id: string }) => void) {
        this.downloadedListeners.add(cb);
        return () => this.downloadedListeners.delete(cb);
    }

    onError(cb: (data: { attachment_id: string; error: string }) => void) {
        this.errorListeners.add(cb);
        return () => this.errorListeners.delete(cb);
    }

    emitProgress(data: DownloadProgress) {
        this.updateProgress(data);
        for (const cb of this.progressListeners) {
            cb(data);
        }
    }

    emitDownloaded(data: { attachment_id: string }) {
        const current = this.downloads.get(data.attachment_id);
        const path =
            current?.status.status === "downloading" ? current.status.path : "";
        this.setDownloaded(data.attachment_id, path);
        const shouldOpen = this.pendingOpen === data.attachment_id;
        if (shouldOpen) {
            this.pendingOpen = undefined;
            window.native.openAttachment(data.attachment_id);
        }
        const shouldShowInFolder =
            this.pendingShowInFolder === data.attachment_id;
        if (shouldShowInFolder) {
            this.pendingShowInFolder = undefined;
            window.native.showAttachmentInFolder(data.attachment_id);
        }
        for (const cb of this.downloadedListeners) {
            cb(data);
        }
    }

    emitError(data: { attachment_id: string; error: string }) {
        this.setError(data.attachment_id);
        for (const cb of this.errorListeners) {
            cb(data);
        }
    }

    initNativeListeners() {
        if (!window.isNative || !window.native) return;

        if (window.native.onDownloadProgress) {
            window.native.onDownloadProgress((data) => {
                this.emitProgress(data);
            });
        }

        if (window.native.onAttachmentDownloaded) {
            window.native.onAttachmentDownloaded((data) => {
                this.emitDownloaded(data);
            });
        }

        if (window.native.onAttachmentDownloadError) {
            window.native.onAttachmentDownloadError((data) => {
                this.emitError(data);
            });
        }
    }
}

const downloadStore = new DownloadStore();

// Initialize native listeners once
let nativeListenersInitialized = false;
export function initNativeDownloadListeners() {
    if (nativeListenersInitialized) return;
    nativeListenersInitialized = true;
    downloadStore.initNativeListeners();
}

export default downloadStore;
